import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { Printer, ArrowLeft, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { sendViaEmail, uploadDocument, buildWhatsAppUrl } from "@/lib/pdfSend";
import { docTexts } from "@/lib/docTexts";

const PAYMENT = { conto_corrente: "Conto corrente", cash: "Cash" };

export default function RicevutaGroup() {
  const { gid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/bookings/group/${gid}/full`).then((r) => setData(r.data)).catch(() => setError("Gruppo non trovato"));
  }, [gid]);

  if (error) return <div className="p-10 text-center text-slate-500">{error}</div>;
  if (!data) return <div className="p-10 text-center text-slate-400">Caricamento…</div>;

  const rooms = data.rooms || [];
  const t = docTexts(data.settings);
  const holder = rooms[0]?.booking || {};
  const struct = rooms[0]?.structure || {};
  const ownerId = holder.id;
  const today = new Date().toLocaleDateString("it-IT");
  const sum = (k) => rooms.reduce((a, r) => a + Number(r.booking[k] || 0), 0);
  const totTotal = sum("total_amount"), totAcconto = sum("acconto"), totSaldo = sum("saldo");
  const allFull = rooms.length > 0 && rooms.every((r) => (r.booking.payment_status || "none") === "full");
  const groupStatus = allFull ? "full" : (totAcconto > 0 ? "partial" : "none");

  const doSend = async (channel) => {
    if (sending) return;
    const el = document.querySelector('[data-testid="ricevuta-sheet"]');
    if (!el) return;
    setSending(true);
    try {
      if (channel === "email") {
        const email = holder.guest_email || window.prompt("Email destinatario:") || "";
        if (!email) { setSending(false); return; }
        await sendViaEmail(ownerId, "ricevuta", el, email);
        toast.success(`Ricevuta inviata a ${email}`);
      } else {
        const phone = holder.guest_phone || window.prompt("Numero WhatsApp (con prefisso, es. +39...):") || "";
        if (!phone) { setSending(false); return; }
        const d = await uploadDocument(ownerId, "ricevuta", el);
        toast.success("Apertura WhatsApp…");
        window.location.href = buildWhatsAppUrl(d.public_url, "ricevuta", phone, `${holder.guest_name} ${holder.guest_surname}`);
      }
    } catch (e) { toast.error("Invio non riuscito"); }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } .sheet { box-shadow: none !important; } }`}</style>
      <div className="max-w-2xl mx-auto mb-4 flex justify-between no-print">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"><ArrowLeft size={16} /> Indietro</button>
        <div className="flex gap-2">
          <button onClick={() => doSend("email")} disabled={sending} data-testid="send-ricevuta-email" className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50"><Mail size={16} /> Email</button>
          <button onClick={() => doSend("whatsapp")} disabled={sending} data-testid="send-ricevuta-whatsapp" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50"><MessageCircle size={16} /> WhatsApp</button>
          <button onClick={() => window.print()} data-testid="print-ricevuta" className="flex items-center gap-2 bg-sky-700 hover:bg-sky-800 text-white text-sm font-semibold px-4 py-2 rounded-md"><Printer size={16} /> Stampa</button>
        </div>
      </div>

      <div className="sheet max-w-2xl mx-auto bg-white p-10 shadow-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="ricevuta-sheet">
        <div className="flex justify-between items-start border-b-2 border-slate-800 pb-3">
          <div className="flex items-center gap-4">
            <img src="/blu-lodge-logo.png" alt="Blu Lodge Maldives" className="w-24 h-24 object-contain shrink-0" data-testid="ricevuta-logo" />
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>{(struct.name || "STRUTTURA").toUpperCase()}</h1>
              <div className="text-xs text-slate-500">Ricevuta di gruppo · {rooms.length} camere{struct.tin ? ` · TIN ${struct.tin}` : ""}</div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500"><div>Data: {today}</div><div>Rif. #{(gid || "").slice(0, 8).toUpperCase()}</div></div>
        </div>

        <div className="py-5">
          <div className="text-xs uppercase tracking-wide text-slate-400">Capogruppo</div>
          <div className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>{`${holder.guest_name || ""} ${holder.guest_surname || ""}`.trim() || "—"}</div>
          <div className="text-xs text-slate-500 mt-1">{holder.checkin} → {holder.checkout}</div>
        </div>

        <table className="w-full text-sm mt-2">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b-2 border-slate-300">
              <th className="py-2">Camera</th><th className="py-2 text-right">Totale</th><th className="py-2 text-right">Acconto</th><th className="py-2 text-right">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map((rm, i) => (
              <tr key={rm.booking.id} className="border-b border-slate-100">
                <td className="py-2 text-slate-700">{i + 1}. {rm.room.name || "—"}{rm.room.type ? ` · ${rm.room.type}` : ""}</td>
                <td className="py-2 text-right tabular font-medium">{eur(rm.booking.total_amount)}</td>
                <td className="py-2 text-right tabular text-emerald-600">{eur(rm.booking.acconto)}</td>
                <td className="py-2 text-right tabular text-amber-600">{eur(rm.booking.saldo)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-800 font-bold">
              <td className="py-3 text-lg text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>Totale gruppo</td>
              <td className="py-3 text-right text-lg tabular text-slate-900">{eur(totTotal)}</td>
              <td className="py-3 text-right tabular text-emerald-700">{eur(totAcconto)}</td>
              <td className="py-3 text-right tabular text-amber-700">{eur(totSaldo)}</td>
            </tr>
          </tbody>
        </table>

        <div className="mt-6" data-testid="ricevuta-payment-status">
          {groupStatus === "full" ? (
            <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 px-4 py-3 text-center">
              <div className="text-lg font-extrabold text-emerald-700 tracking-wide" style={{ fontFamily: "'Manrope', sans-serif" }}>SALDO EFFETTUATO</div>
              <div className="text-xs text-emerald-600 mt-1">Pagamento completato · nessun importo residuo</div>
            </div>
          ) : groupStatus === "partial" ? (
            <div className="rounded-md border-2 border-amber-300 bg-amber-50 px-4 py-3 text-center">
              <div className="text-lg font-extrabold text-amber-700 tracking-wide" style={{ fontFamily: "'Manrope', sans-serif" }}>ACCONTO VERSATO</div>
              <div className="text-sm text-amber-700 mt-1">Ricevuta emessa per il <b>solo acconto</b>. Saldo ancora da corrispondere: <b>{eur(totSaldo)}</b></div>
            </div>
          ) : (
            <div className="rounded-md border-2 border-orange-300 bg-orange-50 px-4 py-3 text-center">
              <div className="text-lg font-extrabold text-orange-700 tracking-wide" style={{ fontFamily: "'Manrope', sans-serif" }}>PAGAMENTO NON ANCORA RICEVUTO</div>
              <div className="text-sm text-orange-700 mt-1">Importo da corrispondere: <b>{eur(totTotal)}</b></div>
            </div>
          )}
        </div>

        <div className="text-sm text-slate-500 mt-4">Metodo acconto: <b className="text-slate-700">{PAYMENT[holder.acconto_method] || "—"}</b> · Metodo saldo: <b className="text-slate-700">{PAYMENT[holder.saldo_method] || "—"}</b></div>
        <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
          {t.policyText}{" "}
          <a href={t.policyUrl} target="_blank" rel="noopener noreferrer" className="text-sky-700 underline break-all">{t.policyUrl}</a>
        </div>
        <div className="mt-3 text-sm font-semibold text-slate-700 leading-relaxed">
          {t.nb}
        </div>
      </div>
    </div>
  );
}
